import { createContext, Dispatch, PropsWithChildren, SetStateAction, useContext, useEffect, useState } from 'react';
import { useAsyncEffect } from './hooks';
import { getAccessTokens, getDummmyAccessTokens, loginUser } from './utilities/LoginUtils';
import { getScope, msalConfig } from './authConfig';
import { useMsal } from '@azure/msal-react';
import { Loader, Segment } from 'semantic-ui-react';
import { InteractionStatus } from '@azure/msal-browser';

interface TenantAccountIds {
  [tenantId: string]: string[];
}

interface IGlobalContext {
  isLoading: boolean;
  tenantId: string;
  setTenantId: Dispatch<SetStateAction<string>>;
  merchantId: string;
  setMerchantId: Dispatch<SetStateAction<string>>;
  accountIds: TenantAccountIds;
  setAccountIds: Dispatch<SetStateAction<TenantAccountIds>>;
  authTokens: Record<string, string>;
  getAuthTokenForPillar: (pillarCode: string) => string;
  setAuthToken: Dispatch<SetStateAction<Record<string, string>>>;
}

const GlobalContext = createContext<IGlobalContext>(null);

const defaultTenantId = import.meta.env.VITE_TENANT_ID;
const defaultMerchantId = import.meta.env.VITE_MERCHANT_ID;

export function GlobalContextProvider({ children }: PropsWithChildren) {
  const [tenantId, setTenantId] = useState<string>(localStorage.getItem(`tenantId`) || defaultTenantId || '');
  const [merchantId, setMerchantId] = useState<string>(localStorage.getItem(`merchantId`) || defaultMerchantId || '');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [accountIds, setAccountIds] = useState<TenantAccountIds>(JSON.parse(localStorage.getItem(`accountIds`)) || {});
  const [authToken, setAuthToken] = useState<Record<string, string>>({});
  const [loggedIn, setLoggedIn] = useState<boolean>(false);
  const { instance, accounts, inProgress } = useMsal();

  useEffect(() => {
    localStorage.setItem(`accountIds`, JSON.stringify(accountIds));
  }, [accountIds, tenantId]);

  useEffect(() => {
    const accountIds = localStorage.getItem(`accountIds`);
    if (accountIds) {
      setAccountIds(JSON.parse(accountIds));
    }
  }, [tenantId]);

  useEffect(() => {
    localStorage.setItem(`tenantId`, tenantId);
  }, [tenantId]);

  useAsyncEffect(async () => {
    if (loggedIn) {
      return;
    }

    if (inProgress !== InteractionStatus.None) {
      return;
    }

    setIsLoading(true);
    if (msalConfig.enabled) {
      const hasAccount = accounts.length > 0;
      await loginUser(hasAccount, instance);

      if (hasAccount) {
        const AuthTokens = await getAccessTokens(instance, accounts[0]);
        setAuthToken(AuthTokens);
        setLoggedIn(true);
      }
    } else {
      //Set the auth token to something so that the useQuery will run, not needed once we move away from Legacy Envs
      setAuthToken(getDummmyAccessTokens());
    }
    setIsLoading(false);
  }, [accounts, inProgress]);

  const getAuthTokenForPillar = (pillarCode) => (authToken ? authToken[getScope(pillarCode)] : undefined);

  return (
    <GlobalContext.Provider
      value={{
        isLoading: isLoading,
        tenantId,
        setTenantId,
        merchantId,
        setMerchantId,
        accountIds,
        setAccountIds,
        authTokens: authToken,
        getAuthTokenForPillar,
        setAuthToken,
      }}>
      {isLoading && (
        <Segment placeholder padded size="huge">
        <Loader active inline="centered">
            Loading tokens
          </Loader>
      </Segment>
      )}
      {!isLoading && children}
    </GlobalContext.Provider>
  );
}

export function useGlobalContext() {
  const tenantContext = useContext(GlobalContext);
  if (!tenantContext) {
    throw new Error('useTenantContext must be used within a TenantContextProvider');
  }
  return tenantContext;
}

export function useAddTenantAccountId() {
  const { tenantId, setAccountIds } = useGlobalContext();
  return (accountId: string) => {
    const addIfNotExists = (accountIds: string[], accountId: string) => {
      if (accountIds.indexOf(accountId) === -1) {
        return [...accountIds, accountId];
      }
      return accountIds;
    };
    setAccountIds((accountIds) => {
      const existingAccountIds = accountIds[tenantId] ? [...accountIds[tenantId]] : [];
      return { ...accountIds, [tenantId]: addIfNotExists(existingAccountIds, accountId) };
    });
  };
}
